import { useApolloClient, useMutation } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { showToast } from '../../graphql/cache/modal';
import { toastVariant } from '../../constants/misc';
import {
  getRiskLabel,
  useDocumentControllerActionItem,
} from './document_controller_action_item';

const contextQuery = loader('./risk_link.context.graphql');
const actionItemsQuery = loader('./risk_link.actionItems.graphql');
const deleteActionItemMutation = loader('./risk_link.deleteActionItem.graphql');

// Linking a risk to one of these notifies the risk's document controllers,
// falling back to the company's default Document Controller role
const recordKeys = {
  incident: 'incidentId',
  correctiveAction: 'cpaId',
};
const closedStatuses = ['DONE', 'DUPLICATE'];
// Action item lists on the record's page, refetched so the change shows instantly
const recordActionItemQueries = {
  incident: ['GetActionItemsForIncident'],
};

// Side effect only, so a failure here shouldn't block the link itself
const notifyOnError =
  (fn, message) =>
  async (...args) => {
    try {
      await fn(...args);
    } catch (error) {
      showToast({
        title: 'Document Controller Action Items',
        message: `${message}: ${error.message}`,
        variant: toastVariant.warning,
      });
    }
  };

export const useRiskLinkActionItems = () => {
  const client = useApolloClient();
  const { workspaceId: currentWorkspaceId } = useWorkspace();
  const { getRoleControllerIds, createControllerActionItem } =
    useDocumentControllerActionItem();
  const [deleteActionItem] = useMutation(deleteActionItemMutation);

  const refetchRecordActionItems = async (modelType) => {
    const include = recordActionItemQueries[modelType];
    if (include) await client.refetchQueries({ include });
  };

  const createLinkActionItems = async ({
    riskRegistryId,
    modelId,
    modelType,
  }) => {
    const recordKey = recordKeys[modelType];
    if (!recordKey) return;

    const isIncident = modelType === 'incident';
    const {
      data: {
        riskRegistry: [risk] = [],
        incidents: [incident] = [],
        correctiveActions: [cpa] = [],
      } = {},
    } = await client.query({
      query: contextQuery,
      fetchPolicy: 'network-only',
      variables: {
        riskRegistryId: `${riskRegistryId}`,
        modelId: `${modelId}`,
        isIncident,
        isCPA: !isIncident,
      },
    });

    const riskControllerIds =
      risk?.documentControllers?.map(({ id }) => id) || [];
    const userIds = riskControllerIds.length
      ? riskControllerIds
      : await getRoleControllerIds();
    if (!userIds.length) return;

    const record = isIncident ? incident : cpa;
    const recordLabel = record?.externalId || (isIncident ? 'Incident' : 'CPA');
    const workspaceId = isIncident
      ? record?.workspaceId
      : record?.interimActionItem?.workspaceId;
    const riskLabel = getRiskLabel(risk);

    await createControllerActionItem({
      userIds,
      failureMessage: 'Risk linked, but the action item could not be created',
      variables: {
        title: `Risk Linked: ${riskLabel} - ${recordLabel}`,
        description: `${recordLabel} has been linked to the risk registry entry "${riskLabel}". Review the entry and update any controlled documents as required.`,
        workspaceId: workspaceId ?? currentWorkspaceId ?? null,
        riskRegistryId: parseInt(riskRegistryId),
        [recordKey]: parseInt(modelId),
      },
    });
    await refetchRecordActionItems(modelType);
  };

  const archiveLinkActionItems = async ({
    riskRegistryId,
    modelId,
    modelType,
  }) => {
    const recordKey = recordKeys[modelType];
    if (!recordKey) return;

    const { data: { actionItems = [] } = {} } = await client.query({
      query: actionItemsQuery,
      fetchPolicy: 'network-only',
      variables: {
        filters: [
          { field: 'type', operator: 'eq', value: ['REGISTRY'] },
          {
            field: 'riskRegistryId',
            operator: 'eq',
            value: [`${riskRegistryId}`],
          },
          { field: recordKey, operator: 'eq', value: [`${modelId}`] },
        ],
      },
    });

    const openItems = actionItems.filter(
      ({ status }) => !closedStatuses.includes(status),
    );
    for (const { id } of openItems) {
      await deleteActionItem({ variables: { id } });
    }
    if (openItems.length) await refetchRecordActionItems(modelType);
  };

  return {
    createLinkActionItems: notifyOnError(
      createLinkActionItems,
      'Risk linked, but action items could not be created',
    ),
    archiveLinkActionItems: notifyOnError(
      archiveLinkActionItems,
      'Risk unlinked, but open action items could not be archived',
    ),
  };
};
